/**
 * ブラウザの音声キャプチャ機能の対応状況を検出するユーティリティ
 */

/**
 * ブラウザの音声機能サポート状況
 */
export interface AudioCapabilities {
  /** getUserMedia APIがサポートされているか */
  supportsGetUserMedia: boolean;
  /** getDisplayMedia APIがサポートされているか */
  supportsGetDisplayMedia: boolean;
  /** AudioContext APIがサポートされているか */
  supportsAudioContext: boolean;
  /** タブ音声キャプチャがサポートされているか（Chrome/Edge） */
  supportsTabAudio: boolean;
}

/**
 * 現在のブラウザの音声キャプチャ機能を検出
 *
 * @returns ブラウザの音声機能サポート状況
 *
 * @example
 * ```typescript
 * const capabilities = detectAudioCapabilities();
 * if (!capabilities.supportsTabAudio) {
 *   console.warn('タブ音声キャプチャには Chrome または Edge が必要です');
 * }
 * ```
 */
export function detectAudioCapabilities(): AudioCapabilities {
  // SSR対応: windowが存在しない場合はすべてfalse
  if (typeof window === 'undefined') {
    return {
      supportsGetUserMedia: false,
      supportsGetDisplayMedia: false,
      supportsAudioContext: false,
      supportsTabAudio: false,
    };
  }

  const caps = {
    // getUserMedia: マイク音声取得
    supportsGetUserMedia: !!(navigator.mediaDevices?.getUserMedia),

    // getDisplayMedia: 画面/タブ共有
    supportsGetDisplayMedia: !!(navigator.mediaDevices?.getDisplayMedia),

    // AudioContext: Web Audio API（ミキシング用）
    supportsAudioContext: !!(
      window.AudioContext ||
      (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    ),

    // タブ音声: Chrome/Edgeのみサポート
    // Firefoxはvideo: trueが必須、Safariは非対応
    supportsTabAudio: /Chrome|Edg/.test(navigator.userAgent),
  };

  return caps;
}

/**
 * タブ音声とマイク音声の同時録音が可能かチェック
 *
 * @returns すべての必要な機能がサポートされている場合true
 *
 * @example
 * ```typescript
 * if (canRecordMixedAudio()) {
 *   // タブ音声 + マイク録音が可能
 * } else {
 *   // マイクのみの録音にフォールバック
 * }
 * ```
 */
export function canRecordMixedAudio(): boolean {
  const caps = detectAudioCapabilities();
  return (
    caps.supportsGetUserMedia &&
    caps.supportsGetDisplayMedia &&
    caps.supportsAudioContext &&
    caps.supportsTabAudio
  );
}

/**
 * AudioWorklet APIがサポートされているかチェック
 *
 * AudioWorkletは別スレッドで動作する音声処理プロセッサー。
 * Chrome 66+, Firefox 76+, Safari 14.1+ でサポート。
 *
 * @returns AudioWorklet APIがサポートされている場合true
 *
 * @example
 * ```typescript
 * if (supportsAudioWorklet()) {
 *   // WAV録音が可能
 * } else {
 *   // MediaRecorder（WebM）にフォールバック
 * }
 * ```
 */
export function supportsAudioWorklet(): boolean {
  // SSR対応
  if (typeof window === 'undefined') {
    return false;
  }

  return (
    typeof AudioWorklet !== 'undefined' &&
    typeof AudioWorkletNode !== 'undefined'
  );
}

/**
 * WAV形式での録音が可能かチェック
 *
 * WAV録音にはAudioWorklet APIが必要。
 * MediaRecorderのWebM形式と異なり、各チャンクが独立したWAVファイルとして生成される。
 *
 * @returns WAV録音がサポートされている場合true
 *
 * @example
 * ```typescript
 * if (canRecordWav()) {
 *   // useWavRecorderを使用
 * } else {
 *   // useAudioRecorder/useMixedAudioRecorderを使用
 * }
 * ```
 */
export function canRecordWav(): boolean {
  const caps = detectAudioCapabilities();
  return (
    caps.supportsGetUserMedia &&
    caps.supportsAudioContext &&
    supportsAudioWorklet()
  );
}

/**
 * MediaRecorderでサポートされている最適なOpus MIMEタイプを取得
 *
 * ブラウザ別の対応状況:
 * - Chrome/Firefox/Edge: audio/webm;codecs=opus
 * - Safari: audio/mp4;codecs=opus (実験的)
 *
 * @returns サポートされている最適なMIMEタイプ、未サポートの場合null
 *
 * @example
 * ```typescript
 * const mimeType = getSupportedOpusMimeType();
 * if (mimeType) {
 *   const recorder = new MediaRecorder(stream, { mimeType });
 * }
 * ```
 */
export function getSupportedOpusMimeType(): string | null {
  // SSR対応: windowやMediaRecorderが存在しない場合はnull
  if (typeof window === 'undefined' || typeof MediaRecorder === 'undefined') {
    return null;
  }

  const candidates = [
    'audio/webm;codecs=opus', // Chrome/Firefox/Edge (preferred)
    'audio/mp4;codecs=opus',  // Safari (experimental)
  ];

  for (const mimeType of candidates) {
    if (
      typeof MediaRecorder.isTypeSupported === 'function' &&
      MediaRecorder.isTypeSupported(mimeType)
    ) {
      return mimeType;
    }
  }

  return null;
}

/**
 * Opus形式での録音が可能かチェック
 *
 * Opusコーデックは音声通話向けの高効率コーデック。
 * WebMコンテナ内でOpusを使用することで、WAVより大幅にファイルサイズを削減できる。
 *
 * @returns Opus録音がサポートされている場合true
 *
 * @example
 * ```typescript
 * if (canRecordOpus()) {
 *   // useOpusRecorderを使用（推奨: API Gateway 32KB制限対応）
 * } else if (canRecordWav()) {
 *   // useWavRecorderを使用
 * } else {
 *   // エラーまたはフォールバック
 * }
 * ```
 */
export function canRecordOpus(): boolean {
  return getSupportedOpusMimeType() !== null;
}

/**
 * 推奨される録音方式を判定
 *
 * API Gateway WebSocketの32KB制限を考慮し、最適な録音方式を選択。
 *
 * @returns 'opus' | 'wav' | 'none'
 *
 * @example
 * ```typescript
 * const method = getRecommendedRecordingMethod();
 * if (method === 'opus') {
 *   // useOpusRecorder（最もサイズ効率が良い）
 * } else if (method === 'wav') {
 *   // useWavRecorder（音質優先）
 * } else {
 *   // ブラウザ未サポート
 * }
 * ```
 */
export function getRecommendedRecordingMethod(): 'opus' | 'wav' | 'none' {
  if (canRecordOpus()) return 'opus';
  if (canRecordWav()) return 'wav';
  return 'none';
}
